import z from "zod";

import { NoticesSortOrder, NoticesTopicSearchSort } from "lib/types/graphQLEnums";
import { NoticeSemanticColumnField, savedNoticeSemanticViewFilters } from "lib/types/savedViews";

/**
 * Hybrid notice sort (Notices V2 topic search)
 *
 * **Visibility (column picker):** `getAllowedSortFieldsForColumns(visibleColumns)` builds the
 * sort dropdown from `relevanceScore`, any `HYBRID_SORTABLE_NOTICE_SEMANTIC_FIELDS` entry in
 * `visibleColumns`, plus `closeDate` and `expiryDate` which are always available (see
 * `ALWAYS_AVAILABLE_SORT_FIELDS`). Pass the same ordered column list that powers the table
 * (URL / saved view / defaults today; column picker state tomorrow).
 *
 * **Adding a new hybrid-backed column later:**
 * 1. Add the `NoticeSemanticColumnField` to `HYBRID_SORTABLE_NOTICE_SEMANTIC_FIELDS`.
 * 2. Extend `semanticFieldToNoticesSort` with the matching `NoticesTopicSearchSort` enum value (must match
 *    hybrid index / `NoticeSortField` in `@stotles/data`).
 * 3. Add labels in `SORT_FIELD_LABELS` and direction copy in `getSemanticSortOrderLabels` if needed.
 */

/** Matches `savedNoticeSemanticView.view.sortOrder` shape. */
export const noticeSemanticSortSchema = savedNoticeSemanticViewFilters.shape.sortOrder;

export type NoticeSemanticSort = z.infer<typeof noticeSemanticSortSchema>;

export const DEFAULT_NOTICE_SEMANTIC_SORT: NoticeSemanticSort = {
  field: "relevanceScore",
  order: "DESC",
};

/**
 * Canonical list of semantic columns that support a non-relevance hybrid sort.
 * Keep in sync with hybrid `NoticeSortField` support in `@stotles/data` (e.g. `noticeHybridSort.ts`).
 * Columns without a hybrid mapping (title, stage, suppliers, actions) must not appear here.
 */
export const HYBRID_SORTABLE_NOTICE_SEMANTIC_FIELDS = [
  "publishDate",
  "closeDate",
  "awardDate",
  "expiryDate",
  "value",
] as const satisfies readonly NoticeSemanticColumnField[];

export type HybridSortableNoticeSemanticField =
  (typeof HYBRID_SORTABLE_NOTICE_SEMANTIC_FIELDS)[number];

export function isHybridSortableSemanticField(
  field: NoticeSemanticColumnField,
): field is HybridSortableNoticeSemanticField {
  return (HYBRID_SORTABLE_NOTICE_SEMANTIC_FIELDS as readonly string[]).includes(field);
}

/**
 * Sort-field keys shown in the "Sort by" field dropdown: relevance plus every hybrid-sortable
 * column that is currently present in `visibleTableColumns`, plus `closeDate` and `expiryDate`
 * which are always available regardless of column visibility (they are the most common date
 * sorts and users should be able to sort by them without needing to show the columns).
 */
const ALWAYS_AVAILABLE_SORT_FIELDS: readonly HybridSortableNoticeSemanticField[] = [
  "closeDate",
  "expiryDate",
];

export function getAllowedSortFieldsForColumns(
  visibleTableColumns: NoticeSemanticColumnField[],
): NoticeSemanticColumnField[] {
  const visible = new Set(visibleTableColumns);
  return [
    "relevanceScore",
    ...HYBRID_SORTABLE_NOTICE_SEMANTIC_FIELDS.filter(
      (f) => visible.has(f) || ALWAYS_AVAILABLE_SORT_FIELDS.includes(f),
    ),
  ];
}

/**
 * Returns the most useful default sort direction for a given field, used when switching fields
 * in the sort picker so the direction auto-resets to the most natural ordering.
 */
export function getDefaultSortOrderForField(
  field: NoticeSemanticColumnField,
): NoticeSemanticSort["order"] {
  switch (field) {
    case "closeDate":
    case "expiryDate":
    case "awardDate":
      return "ASC";
    default:
      return "DESC";
  }
}

export function coerceNoticeSemanticSort(
  sort: NoticeSemanticSort,
  visibleTableColumns: NoticeSemanticColumnField[],
): NoticeSemanticSort {
  const allowed = new Set(getAllowedSortFieldsForColumns(visibleTableColumns));
  if (allowed.has(sort.field)) {
    return sort;
  }
  return { ...DEFAULT_NOTICE_SEMANTIC_SORT };
}

export function semanticFieldToNoticesSort(
  field: NoticeSemanticColumnField,
): NoticesTopicSearchSort {
  switch (field) {
    case "relevanceScore":
      return NoticesTopicSearchSort.Score;
    case "publishDate":
      return NoticesTopicSearchSort.PublishedDate;
    case "closeDate":
      return NoticesTopicSearchSort.CloseDate;
    case "awardDate":
      return NoticesTopicSearchSort.AwardDate;
    case "expiryDate":
      return NoticesTopicSearchSort.ExpiryDate;
    case "value":
      return NoticesTopicSearchSort.ValueAmount;
    default:
      return NoticesTopicSearchSort.Score;
  }
}

export function noticesSortOrderToGraphQLEnum(
  order: NoticeSemanticSort["order"],
): NoticesSortOrder {
  return order === "ASC" ? NoticesSortOrder.Asc : NoticesSortOrder.Desc;
}

const SORT_FIELD_LABELS: Record<NoticeSemanticColumnField, string> = {
  relevanceScore: "Relevance",
  title: "Title & buyer",
  stage: "Stage",
  value: "Value",
  suppliers: "Supplier(s)",
  actions: "Actions",
  awardDate: "Award date",
  closeDate: "Close date",
  expiryDate: "Expiry date",
  publishDate: "Publish date",
};

export function getSemanticSortFieldLabel(field: NoticeSemanticColumnField): string {
  return SORT_FIELD_LABELS[field];
}

type OrderLabels = { asc: string; desc: string };

export function getSemanticSortOrderLabels(field: NoticeSemanticColumnField): OrderLabels {
  switch (field) {
    case "relevanceScore":
      return { desc: "Most to least", asc: "Least to most" };
    case "value":
      return { desc: "Highest to lowest", asc: "Lowest to highest" };
    case "publishDate":
      return { desc: "Newest to oldest", asc: "Oldest to newest" };
    case "closeDate":
      return { desc: "Latest to earliest", asc: "Earliest to latest" };
    case "expiryDate":
      return { desc: "Latest to earliest", asc: "Earliest to latest" };
    case "awardDate":
      return { desc: "Newest first", asc: "Oldest first" };
    default:
      return { desc: "Descending", asc: "Ascending" };
  }
}
